import {Coordinates} from "sigma/types";
import {OverlayResult, OverlayResults} from "../types/overlay";

import {Bounds, Region} from "../types/Types";
import {hexToStringRgba, pvalueToAlphaLog} from "./colour";
import {drawSmoothQuadraticCurvePath} from "./draw";

export function makeCoordinateMapper(
  bounds: Bounds,
  canvas: HTMLCanvasElement,
  paddingRatio = 0.1 // 10% padding
): (coord: Coordinates) => Coordinates {
  const {minX, maxX, minY, maxY} = bounds;
  const dpr = window.devicePixelRatio || 1;

  const dataWidth = maxX - minX;
  const dataHeight = maxY - minY;

  const canvasWidth = canvas.width / dpr;
  const canvasHeight = canvas.height / dpr;

  const innerWidth = canvasWidth * (1 - 2 * paddingRatio);   // 80% of canvas
  const innerHeight = canvasHeight * (1 - 2 * paddingRatio);

  // independent scaling in X and Y so data fits inside padded box
  const scaleX = innerWidth / dataWidth;
  const scaleY = innerHeight / dataHeight;
  const scale = Math.min(scaleX, scaleY); // uniform scale

  // center of data in data space
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;

  // center of inner drawable area
  const innerCenterX = canvasWidth / 2;
  const innerCenterY = canvasHeight / 2;

  return ({x, y}: Coordinates): Coordinates => ({
    x: (x - cx) * scale + innerCenterX,
    y: (cy - y) * scale + innerCenterY,
  });
}

export function scaleCoordinate(mapToCanvas: (coord: Coordinates) => Coordinates, coordinate: Coordinates) {
  const origin = mapToCanvas({x: 0, y: 0});
  const diagonal = mapToCanvas(coordinate);

  return Math.max(diagonal.x - origin.x, diagonal.y - origin.y);
}

export function scaleNumber(mapToCanvas: (coord: Coordinates) => Coordinates, radius: number) {
  return scaleCoordinate(mapToCanvas, {x: radius, y: radius});
}

export function drawNetworkOutline(
  ctx: CanvasRenderingContext2D,
  mapToCanvas: (coord: Coordinates) => Coordinates,
  bounds: Bounds
): void {
  const viewportRadius = scaleCoordinate(mapToCanvas, {
    x: (bounds.maxX - bounds.minX) / 2,
    y: (bounds.maxY - bounds.minY) / 2
  }) * 1.1;

  const centre = mapToCanvas({
    x: bounds.minX + (bounds.maxX - bounds.minX) / 2,
    y: bounds.minY + (bounds.maxY - bounds.minY) / 2
  })

  ctx.save();
  ctx.strokeStyle = "gray"; // Green path
  ctx.setLineDash([5, 5]);
  ctx.beginPath();
  ctx.arc(centre.x, centre.y, viewportRadius, 0, 2 * Math.PI);
  ctx.stroke();
  ctx.restore();
}

export function drawRegions(
  ctx: CanvasRenderingContext2D,
  mapToCanvas: (coord: Coordinates) => Coordinates,
  regions: Array<Region>,
): void {
  ctx.save();
  ctx.lineWidth = 1;
  ctx.setLineDash([5, 5]);
  ctx.fillStyle = "black";
  ctx.strokeStyle = "gray";

  regions.forEach((region) => {
    const vertices = region.outline.map(value => mapToCanvas(value as Coordinates));
    drawSmoothQuadraticCurvePath(ctx, vertices);
  });

  ctx.restore();
}

export function drawRegionsEnriched(
  ctx: CanvasRenderingContext2D,
  mapToCanvas: (coord: Coordinates) => Coordinates,
  regions: Array<Region>,
  hoveredRegions: Array<Region>
): void {
  regions.forEach(region => {
    const vertices = region.outline.map(value => mapToCanvas(value as Coordinates));

    ctx.save();

    ctx.strokeStyle = region.colour;
    ctx.lineWidth = 3;

    /* GLOWUP for hovered regions */
    if (hoveredRegions.includes(region)) {
      ctx.shadowColor = region.colour;
      ctx.shadowBlur = 30;
    }

    drawSmoothQuadraticCurvePath(ctx, vertices);

    ctx.restore();
  })
}

export function drawOverlay(
  ctx: CanvasRenderingContext2D,
  mapToCanvas: (coord: Coordinates) => Coordinates,
  geneToNode: Record<string, Coordinates>,
  overlayResults: Array<OverlayResult>
): void {
  ctx.save();

  const r = scaleNumber(mapToCanvas, 50);

  overlayResults.forEach(overlayResult => {
    overlayResult.genes.forEach(gene => {
      const node = geneToNode[gene.name];
      if (!node) return;

      const {x, y} = mapToCanvas(node as Coordinates);

      // Draw gene node circle with color and alpha based on p-value
      ctx.fillStyle = hexToStringRgba(overlayResult.colour, pvalueToAlphaLog(gene.pvalue, 0.1, 1));
      ctx.beginPath();
      ctx.arc(x, y, r, 0, 2 * Math.PI);
      ctx.fill();
      ctx.closePath();
    });
  });

  ctx.restore();
}

export function drawOverlayAll(
  canvas: HTMLCanvasElement,
  bounds: Bounds,
  regions: Array<Region> | undefined,
  hoveredRegions: Array<Region>,
  overlayResults: OverlayResults | undefined,
  regionLookup: Record<string, Region>,
  geneToNode: Record<string, Coordinates>,
): void {
  const ctx = canvas.getContext("2d");

  if (!ctx) return;

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const mapToCanvas = makeCoordinateMapper(bounds, canvas);

  // Draw big circle outline
  drawNetworkOutline(ctx, mapToCanvas, bounds);

  if (!regions) return;

  // Draw non-enriched regions
  const greyRegions = regions.filter((region) =>
    !(region.name.replaceAll("\n", " ") in (
      overlayResults?.enrichment.enriched_domains ?? {})
    )
  );

  drawRegions(ctx, mapToCanvas, greyRegions);

  if (!overlayResults?.overlay.length) return;

  // Draw enriched regions
  const enrichedRegions = Object.keys(overlayResults.enrichment.enriched_domains)
    .map(domain => regionLookup[domain])
    .filter(r => r !== undefined);

  drawRegionsEnriched(ctx, mapToCanvas, enrichedRegions, hoveredRegions);

  // Draw overlay enrichment
  drawOverlay(ctx, mapToCanvas, geneToNode, overlayResults.overlay);
}